/* =========================
   PRODUCT-SCOPED STORAGE HELPERS
   NOTE: requires a data-product-id="{{ product.id }}" attribute
   on #eumePersonalizeCard (or #personalizeModal, or any element
   with [data-product-id]) somewhere in the product template.
========================= */

function getCurrentProductId() {
  return (
    document.querySelector('#eumePersonalizeCard')?.dataset.productId ||
    document.querySelector('#personalizeModal')?.dataset.productId ||
    document.querySelector('[data-product-id]')?.dataset.productId ||
    null
  );
}

function getPersonalisationStorageKey() {
  const productId = getCurrentProductId();
  // Falls back to the old flat key if no product id is found,
  // so nothing breaks if the data attribute is missing —
  // but you won't get per-product scoping until it's added.
  return productId
    ? `eumePersonalisation_${productId}`
    : 'eumePersonalisation';
}

const MAX_PERSONALISATION_LENGTH = 10;

function readStoredPersonalisation() {
  try {
    const storageKey = getPersonalisationStorageKey();
    const stored = JSON.parse(localStorage.getItem(storageKey));
    const text = typeof stored?.text === 'string'
      ? stored.text.trim().slice(0, MAX_PERSONALISATION_LENGTH)
      : '';

    if (!text) return null;

    // Older saved entries did not always retain a bundle key. Generate one
    // once and persist it so the paid luggage and free personalisation item
    // continue to share the same stable relationship in Shopify.
    if (!stored.bundleKey) {
      stored.bundleKey = Date.now().toString();
      stored.text = text;
      localStorage.setItem(storageKey, JSON.stringify(stored));
    }

    return {
      text,
      bundleKey: String(stored.bundleKey)
    };
  } catch (error) {
    return null;
  }
}

function isCurrentProductForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;

  const currentProductId = getCurrentProductId();
  const formProductId = form.closest('product-form-component')?.dataset.productId;

  return Boolean(
    currentProductId &&
    formProductId === currentProductId &&
    form.querySelector('[name="properties[Personalisation]"]') &&
    form.querySelector('[name="properties[_Personalisation]"]') &&
    form.querySelector('[name="properties[_BundleKey]"]')
  );
}

function syncPersonalisationToForm(form, stored = readStoredPersonalisation()) {
  if (!stored || !isCurrentProductForm(form)) return false;

  form.querySelector('[name="properties[Personalisation]"]').value = stored.text;
  form.querySelector('[name="properties[_Personalisation]"]').value = 'Yes';
  form.querySelector('[name="properties[_BundleKey]"]').value = stored.bundleKey;

  return true;
}

function syncPersonalisationToCurrentForm(stored = readStoredPersonalisation()) {
  const currentProductId = getCurrentProductId();
  if (!currentProductId || !stored) return false;

  const productForm = Array.from(
    document.querySelectorAll('product-form-component[data-product-id] form')
  ).find((form) => isCurrentProductForm(form));

  return productForm ? syncPersonalisationToForm(productForm, stored) : false;
}

// The theme's product form can be refreshed after a variant change. Reapply
// the saved values at the last authoritative moment, before Shopify builds
// FormData. This also covers the sticky add-to-cart button because it delegates
// to this same native product form.
document.addEventListener('submit', (event) => {
  const form = event.target;
  const stored = readStoredPersonalisation();

  if (stored && syncPersonalisationToForm(form, stored)) {
    localStorage.setItem('eumePersonalisationPending', 'true');
  }
}, true);

document.addEventListener('DOMContentLoaded', () => {

  const modal = document.querySelector('#personalizeModal');
  const card = document.querySelector('#eumePersonalizeCard');
  const saved = document.querySelector('#eumePersonalizeSaved');

  const input = document.querySelector('#personalizeInput');
  const preview = document.querySelector('#previewText');
  const count = document.querySelector('#charCount');
  const savedText = document.querySelector('#savedPersonalisationText');

  function openModal() {
    modal?.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    modal?.classList.add('hidden');
    document.body.style.overflow = '';
  }

  /* OPEN */

  card?.addEventListener('click', openModal);

  /* CLOSE */

  document.querySelector('.eume-close')
    ?.addEventListener('click', closeModal);

  document.querySelector('#cancelPersonalisation')
    ?.addEventListener('click', closeModal);

  document.querySelector('.eume-modal-overlay')
    ?.addEventListener('click', closeModal);

  /* LIVE PREVIEW */
  const modalButtons = document.querySelector('.modal-buttons');
  const MAX_PERSONALIZE_LEN = MAX_PERSONALISATION_LENGTH;

  function syncPersonalizeInput() {

    if (input.value.length > MAX_PERSONALIZE_LEN) {
      input.value = input.value.slice(0, MAX_PERSONALIZE_LEN);
    }

    preview.textContent = input.value;

    count.textContent =
      `${input.value.length}/10`;

    if (input.value.trim().length > 0) {
      modalButtons.classList.add('has-text');
    } else {
      modalButtons.classList.remove('has-text');
    }

  }

  input?.addEventListener('input', syncPersonalizeInput);

  // Paste can insert text longer than maxlength before 'input' reflects it
  input?.addEventListener('paste', () => {
    setTimeout(syncPersonalizeInput, 0);
  });

  // Dragging text into the field
  input?.addEventListener('drop', () => {
    setTimeout(syncPersonalizeInput, 0);
  });

  // Autofill sometimes only fires 'change', or fires late/silently
  input?.addEventListener('change', syncPersonalizeInput);

  // Safety net for autofill that skips events entirely on some iOS versions
  setTimeout(syncPersonalizeInput, 300);

  /* SAVE */

  document.querySelector('#savePersonalisation')
    ?.addEventListener('click', () => {

      const value = input.value.trim();

      if (!value) return;

      const bundleKey =
        Date.now().toString();

      document.querySelector('#personalisation-property').value = value;
      document.querySelector('#personalisation-enabled').value = 'Yes';
      document.querySelector('#bundle-key').value = bundleKey;

      localStorage.setItem(
        getPersonalisationStorageKey(),
        JSON.stringify({
          text: value,
          bundleKey: bundleKey
        })
      );

      localStorage.setItem(
        'eumePersonalisationPending',
        'true'
      );

      closeModal();

      const successPopup =
        document.querySelector(
          '#personalisationSuccess'
        );

      successPopup.classList.remove(
        'hidden'
      );

      setTimeout(() => {

        successPopup.classList.add(
          'hidden'
        );

        card.classList.add(
          'hidden'
        );

        if (savedText) {
          savedText.textContent = `Added: ${value}`;
        }

        saved.classList.remove(
          'hidden'
        );

      }, 2000);

      const checkbox =
        document.querySelector('#personalizeCheckbox');

      if (checkbox) {
        checkbox.checked = true;
      }

    });

  /* EDIT */

  document.querySelector('#editPersonalisation')
    ?.addEventListener('click', () => {

      openModal();

    });

  /* REMOVE */

  document.querySelector('#removePersonalisation')
    ?.addEventListener('click', () => {

      document.querySelector('#personalisation-property').value = '';
      document.querySelector('#personalisation-enabled').value = '';
      document.querySelector('#bundle-key').value = '';

      preview.textContent = '';

      input.value = '';

      count.textContent =
      `${input.value.length}/10`;

      localStorage.removeItem(
        getPersonalisationStorageKey()
      );

      if (savedText) {
        savedText.textContent = 'Added: ';
      }

      saved.classList.add('hidden');
      card.classList.remove('hidden');

      const checkbox =
        document.querySelector('#personalizeCheckbox');

      if (checkbox) {
        checkbox.checked = false;
      }

    });

  /* RESTORE SAVED STATE ON LOAD (if personalisation already stored FOR THIS PRODUCT) */

  try {
    const stored = readStoredPersonalisation();

    if (stored) {

      // Keep the visible saved state and the values submitted to Shopify in
      // lockstep. Previously only the visible "Added" state was restored.
      syncPersonalisationToCurrentForm(stored);

      if (input) {
        input.value = stored.text;
        syncPersonalizeInput();
      }

      if (savedText) {
        savedText.textContent = `Added: ${stored.text}`;
      }

      card?.classList.add('hidden');
      saved?.classList.remove('hidden');

      const checkbox = document.querySelector('#personalizeCheckbox');
      if (checkbox) {
        checkbox.checked = true;
      }

    }
  } catch (e) {
    // no stored personalisation, ignore
  }

});

/* =========================
   CART REFRESH
========================= */

async function refreshCartUI() {

  try {

    const cart = await fetch('/cart.js')
      .then(r => r.json());

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart
        }
      })
    );

  } catch (e) {

    console.error('Cart refresh failed', e);

  }

}


/* =========================
   FREE PRODUCT ADD
========================= */

let freebieAdded = false;

document.addEventListener(
  'cart:update',
  async function(event) {

    const pending =
      localStorage.getItem(
        'eumePersonalisationPending'
      );

    if (pending !== 'true') {
      return;
    }

    if (freebieAdded) {
      return;
    }

    freebieAdded = true;

    try {

      const stored =
        JSON.parse(
          localStorage.getItem(
            getPersonalisationStorageKey()
          )
        );

      if (!stored) {
        freebieAdded = false;
        return;
      }

      const variantId =
        document.querySelector(
          '#freebie-variant-id'
        )?.value;

      if (!variantId) {
        freebieAdded = false;
        return;
      }

      const response =
        await fetch('/cart/add.js', {

          method: 'POST',

          headers: {
            'Content-Type': 'application/json'
          },

          body: JSON.stringify({

            id: Number(variantId),

            quantity: 1,

            properties: {

              '_Freebie': 'Yes',

              '_BundleKey':
              stored.bundleKey,

              'Personalisation':
              stored.text

            }

          })

        });

      const item =
        await response.json();

      const cart =
        await fetch('/cart.js')
        .then(r => r.json());

      document.dispatchEvent(
        new CustomEvent(
          'cart:update',
          {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'personalisation',
              data: {
                itemCount:
                cart.item_count,
                variantId:
                item.id
              }
            }
          }
        )
      );

      localStorage.removeItem(
        'eumePersonalisationPending'
      );

    } catch(error) {

      console.error(
        'Freebie add error',
        error
      );

    }

    setTimeout(() => {
      freebieAdded = false;
    }, 1000);

  }
);

/* =========================
   FREEBIE CLEANUP
========================= */

async function syncPersonalisationProducts() {

  try {

    const cart =
      await fetch('/cart.js')
      .then(r => r.json());

    const bundleKeys = [];

    cart.items.forEach(item => {

      if (
        item.properties &&
        item.properties._Personalisation === 'Yes'
      ) {

        bundleKeys.push(
          item.properties._BundleKey
        );

      }

    });

    const updates = {};

    cart.items.forEach(item => {

      if (
        item.properties &&
        item.properties._Freebie === 'Yes'
      ) {

        const bundleKey =
          item.properties._BundleKey;

        if (
          !bundleKeys.includes(bundleKey)
        ) {

          updates[item.key] = 0;

        }

      }

    });

    if (
      Object.keys(updates).length
    ) {

      await fetch('/cart/update.js', {

        method: 'POST',

        headers: {
          'Content-Type': 'application/json'
        },

        body: JSON.stringify({
          updates
        })

      });

      await refreshCartUI();

    }

  } catch (e) {

    console.error(
      'Freebie cleanup error',
      e
    );

  }

}

document.addEventListener(
  'cart:update',
  syncPersonalisationProducts
);

window.addEventListener(
  'load',
  syncPersonalisationProducts
);
